<template>
  <div
    :id="clickable ? buttonId : undefined"
    class="flex h-full flex-col overflow-hidden rounded-3xl border border-solid border-gray-500 bg-white transition-shadow"
    :class="[
      clickable &&
        'group cursor-pointer hover:shadow-card hover:shadow-blue-400_a3',
      hasShadow && 'shadow-card',
      hasMinHeight && 'min-h-[400px]',
    ]"
    :title="clickable ? title : ''"
    @click="handleClick"
  >
    <TheMedia
      :title="title"
      :description="description"
      :video-data="videoData"
      :thumbnail="imageUrl"
      class="aspect-video w-full rounded-3xl"
    />
    <div class="flex grow flex-col justify-between p-4 tablet:p-5">
      <div class="tablet:min-h-32">
        <div class="mb-2 flex items-center justify-between gap-2">
          <h3 class="text-h5 truncate">
            {{ title }}
          </h3>
          <div
            v-if="tag"
            class="shrink-0 rounded bg-gray-600_a15 px-2.5 py-1 text-sm"
          >
            {{ tag }}
          </div>
        </div>

        <p class="mb-4 line-clamp-3 text-sm leading-6 text-gray-700">
          {{ description }}
        </p>
      </div>

      <div class="mt-auto flex items-center justify-between gap-2">
        <div class="shrink-0">
          <TheNuxtLink
            v-if="buttonName && buttonUrl"
            :id="!clickable ? buttonId : undefined"
            ref="linkRef"
            :to="buttonUrl"
            :title="!clickable ? buttonName : ''"
            :class="!clickable && 'hover:underline'"
            class="inline-flex w-fit items-center truncate text-sm font-bold leading-6 text-blue-400 tablet:text-base"
          >
            {{ buttonName }}
          </TheNuxtLink>
        </div>

        <ul
          v-if="contextLogos.length > 0"
          class="scrollbar-h-px flex items-center gap-2 overflow-y-hidden overflow-x-scroll"
        >
          <li v-for="logo in contextLogos" :key="logo" class="shrink-0">
            <img
              :src="logo"
              :alt="title"
              class="pointer-events-none block h-10"
            />
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import type { RouteLocationRaw } from 'vue-router';

  import type { IVideoData } from '@/components/Shared/Lightbox/Video.vue';
  import TheNuxtLink from '@/components/TheNuxtLink.vue';

  interface IProps {
    title: string;
    description: string;
    imageUrl: string;
    videoUrl?: string;
    logos?: string[];
    tag?: string;
    buttonId?: string;
    buttonUrl?: RouteLocationRaw;
    buttonName?: string;
    clickable?: boolean;
    hasShadow?: boolean;
    hasMinHeight?: boolean;
  }

  const props = withDefaults(defineProps<IProps>(), {
    videoUrl: undefined,
    logos: () => [],
    tag: undefined,
    buttonId: undefined,
    buttonUrl: undefined,
    buttonName: undefined,
    clickable: false,
    hasShadow: true,
    hasMinHeight: true,
  });

  const contextLogos = computed((): string[] =>
    (props.logos ?? []).slice(0, 2)
  );

  const videoData = computed((): IVideoData | undefined =>
    props.videoUrl
      ? {
          title: props.title,
          description: props.description,
          url: props.videoUrl,
          imageUrl: props.imageUrl,
          buttonPosition: 'center',
        }
      : undefined
  );

  const linkRef = ref<InstanceType<typeof TheNuxtLink> | null>(null);

  const handleClick = (event: MouseEvent): void => {
    const linkEl = linkRef.value?.$el as HTMLElement | undefined;
    const isOutsideLink = linkEl && !linkEl.contains(event.target as Node);
    // 避免點擊連結本身時再次觸發，造成無窮迴圈
    if (props.clickable && props.buttonUrl && isOutsideLink) {
      linkEl.click();
    }
  };
</script>
